import React from "react";
import { useSelector } from "react-redux";

import { PricingOptions } from "../../../../components";
import { GameInformationPricingAndAvailabilityDataType } from "../../../../models/forms";
import { getRatings } from "../../../../redux/selectors";
import { FieldOption } from "../../../../server/crm/api/models/forms";
import {
  LanguageItem,
  useLanguagesList,
} from "../../../GameInformationPage/PricingAndAvailabilitySubForm/languagesList";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";
import { SensitiveContentStatic } from "../../../../server/crm/dataService/models/staticOptions";

type ReadOnlyIdXboxAvailabilityPricingProps = {
  dataInForm: GameInformationPricingAndAvailabilityDataType;
};

const IdXboxAvailabilityPricingConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "supportedLanguages",
    {
      mapper: (
        data: GameInformationPricingAndAvailabilityDataType,
        _localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) => {
        if (!additionalData) {
          return null;
        }
        const languagesList = additionalData.get("languagesList") as LanguageItem[];
        return data.supportedLanguages.map(
          language => languagesList.find(item => item.value === language)?.label
        );
      },
    },
  ],
  ["otherLanguages", {}],
  [
    "monetizationStrategy",
    {
      title: "monetization",
      mapper: (
        data: GameInformationPricingAndAvailabilityDataType,
        localizedStrings: Map<string, string>
      ) =>
        localizedStrings.get(
          data.monetizationStrategy === PricingOptions.FreeToPlay ? "freeToPlay" : "premium"
        ),
    },
  ],
  [
    "howItWillGenerateMoney",
    {
      title: "indicateHowGameWillGenerateRevenue",
    },
  ],
  [
    "retentionMechanics",
    {
      title: "whatRetentionMechanicsAreInGame",
    },
  ],
  [
    "inGamePricePoints",
    {
      title: "describeYourInGamePricePoints",
    },
  ],
  [
    "whatPlayersCanSpendOn",
    {
      title: "describeWhatPlayersCanSpendOn",
    },
  ],
  [
    "timeUntilAPlayerIsPromptedToPay",
    {
      title: "howLongUntilANewPlayerWillPay",
    },
  ],
  [
    "howItemsCanBeGifted",
    {
      title: "describeHowItemsCanBeGifted",
    },
  ],
  [
    "subscriptionModel",
    {
      title: "describeYourSubscriptionModel",
    },
  ],
  [
    "anticipatedRatings",
    {
      mapper: (
        data: GameInformationPricingAndAvailabilityDataType,
        _localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) => {
        if (!additionalData) {
          return null;
        }
        const crmRatingsList = additionalData.get("crmRatingsList") as FieldOption[];
        return crmRatingsList.find(ratingItem => ratingItem.id === data.anticipatedRatings)?.value;
      },
    },
  ],
  [
    "sensitiveContent",
    {
      title: "sensitiveContentReadOnlyTitle",
      mapper: (
        data: GameInformationPricingAndAvailabilityDataType,
        localizedStrings: Map<string, string>
      ) => {
        const sensitiveContent = data.sensitiveContent;
        let locKey = "other";
        switch (sensitiveContent) {
          case SensitiveContentStatic.Yes.toString():
            locKey = "yes";
            break;
          case SensitiveContentStatic.No.toString():
            locKey = "no";
            break;
          case SensitiveContentStatic.Maybe.toString():
            locKey = "maybe";
            break;
          case SensitiveContentStatic.Other.toString():
            locKey = "other";
            break;
        }
        return localizedStrings.get(locKey);
      },
    },
  ],
  [
    "sensitiveContentDetails",
    {
      title: "sensitiveContentDetailsReadOnlyTitle",
    },
  ],
]);

export const ReadOnlyIdXboxAvailabilityPricing = ({
  dataInForm,
}: ReadOnlyIdXboxAvailabilityPricingProps) => {
  const extraStringsToLocalize = [
    "monetization",
    "suggestedRetailPrice",
    "indicateHowGameWillGenerateRevenue",
    "whatRetentionMechanicsAreInGame",
    "describeYourInGamePricePoints",
    "describeWhatPlayersCanSpendOn",
    "howLongUntilANewPlayerWillPay",
    "describeHowItemsCanBeGifted",
    "describeYourSubscriptionModel",
    "freeToPlay",
    "premium",
    "yes",
    "no",
    "maybe",
    "other",
  ];

  const languagesList = useLanguagesList();
  const crmRatingsList = useSelector(getRatings);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalData = new Map<string, any>([
    ["languagesList", languagesList],
    ["crmRatingsList", crmRatingsList],
  ]);

  return (
    <ReadOnlyForm
      formConfig={IdXboxAvailabilityPricingConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
      additionalData={additionalData}
    />
  );
};
